import type { ApiCourseLevel, ApiDifficulty } from '@/api/types';
import { topicBySlug } from '@/data/topics';

/**
 * SEO title/description shablonlari (faqat o'zbekcha — prerender tili).
 * Sof funksiyalar: prerender (server) va SPA navigatsiyasi bir xil natija beradi.
 */

export const MAX_TITLE = 60;
export const MAX_DESCRIPTION = 155;

const SITE_SUFFIX = ' | SQLMaster.uz';
const PROBLEM_SUFFIX = ' — SQL masala';

export const DIFFICULTY_UZ: Record<ApiDifficulty, string> = {
  EASY: 'oson',
  MEDIUM: "o'rta",
  HARD: 'qiyin',
};

/** `max` belgidan oshmaydigan qilib so'z chegarasida kesadi (oxiriga "…", u ham hisobda). */
export function truncateAtWord(text: string, max: number): string {
  const t = text.trim();
  if (t.length <= max) return t;
  const hard = t.slice(0, max - 1);
  const space = hard.lastIndexOf(' ');
  // Juda uzun so'z bo'lsa (bo'sh joy boshida) — qattiq kesamiz
  const cut = space >= (max - 1) * 0.6 ? hard.slice(0, space) : hard;
  return `${cut.replace(/[\s,;:.\-—–]+$/, '')}…`;
}

/**
 * "{title} — SQL masala | SQLMaster.uz" (≤60). Sig'masa avval " | SQLMaster.uz" tashlanadi,
 * keyin sarlavha so'z chegarasida qisqartiriladi.
 */
export function problemSeoTitle(title: string): string {
  const t = title.trim();
  const full = `${t}${PROBLEM_SUFFIX}${SITE_SUFFIX}`;
  if (full.length <= MAX_TITLE) return full;
  const short = `${t}${PROBLEM_SUFFIX}`;
  if (short.length <= MAX_TITLE) return short;
  return `${truncateAtWord(t, MAX_TITLE - PROBLEM_SUFFIX.length)}${PROBLEM_SUFFIX}`;
}

/** Ro'yxat sahifalari: "{name} | SQLMaster.uz" (sig'masa suffix'siz, keyin qisqartirilgan). */
export function pageSeoTitle(name: string): string {
  const full = `${name}${SITE_SUFFIX}`;
  return full.length <= MAX_TITLE ? full : truncateAtWord(name, MAX_TITLE);
}

const isFence = (line: string) => /^\s*```/.test(line);
const isTableRow = (line: string) => /^\s*\|.*\|\s*$/.test(line);
const isHeading = (line: string) => /^\s*#{1,6}\s/.test(line);

/**
 * Masala shartining kirish qismi (sof matn): kod bloklari tashlanadi, birinchi
 * sarlavha/jadvalgacha bo'lgan paragraflar olinadi ("### Jadval:" sxemasi kirmaydi).
 */
export function statementLead(markdown: string | null | undefined): string {
  if (!markdown) return '';
  const out: string[] = [];
  let inFence = false;
  for (const line of markdown.replace(/\r/g, '').split('\n')) {
    if (isFence(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    if (isHeading(line) || isTableRow(line)) {
      if (out.length > 0) break;
      continue;
    }
    const text = line.trim().replace(/^[-*]\s+/, '');
    if (text) out.push(text);
  }
  return out
    .join(' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/\*\*([^*]*)\*\*/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    // "_" olib tashlanmaydi — jadval/ustun nomlarida bor (daily_sales)
    .replace(/[*>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

interface DescriptionSource {
  title: string;
  description: string | null;
  difficulty: ApiDifficulty;
  topics: string[];
}

/**
 * meta description (≤155): shartning birinchi jumla(lar)i so'z chegarasida +
 * " Qiyinlik: … Mavzu: …". Shart yo'q (premium) bo'lsa — sarlavhadan jumla.
 */
export function problemSeoDescription(p: DescriptionSource): string {
  const topics = p.topics.map((t) => topicBySlug(t)?.title.uz ?? t).join(', ');
  const suffix = ` Qiyinlik: ${DIFFICULTY_UZ[p.difficulty]}.${topics ? ` Mavzu: ${topics}.` : ''}`;
  const budget = MAX_DESCRIPTION - suffix.length;

  const lead = statementLead(p.description);
  if (!lead) {
    return truncateAtWord(`«${p.title.trim()}» — SQL masala. Yechimingizni onlayn tekshiring.`, budget) + suffix;
  }

  // Jumla oxiri: . ! ? … (lekin "1." kabi ro'yxat raqami emas)
  const sentences = lead.split(/(?<=[^\d\s][.!?…])\s+/);
  let body = '';
  for (const s of sentences) {
    const next = body ? `${body} ${s}` : s;
    if (next.length > budget) {
      // Joy yetarli qolgan bo'lsa — keyingi jumla so'z chegarasida qisqartirib qo'shiladi
      const room = budget - (body ? body.length + 1 : 0);
      if (room >= 40) body = body ? `${body} ${truncateAtWord(s, room)}` : truncateAtWord(s, room);
      break;
    }
    body = next;
  }
  if (!body) body = truncateAtWord(sentences[0], budget);
  return body + suffix;
}

/* ------------------------------------------------------------- Kurslar */

const COURSE_SUFFIX = ' — video kurs';
const CAREER_SUFFIX = " — kasb yo'nalishi";
const LESSON_SUFFIX = ' — video dars';

/** "{title}{suffix} | SQLMaster.uz" (≤60) — problemSeoTitle bilan bir xil qisqartirish tartibi. */
function suffixedSeoTitle(title: string, suffix: string): string {
  const t = title.trim();
  const full = `${t}${suffix}${SITE_SUFFIX}`;
  if (full.length <= MAX_TITLE) return full;
  const short = `${t}${suffix}`;
  if (short.length <= MAX_TITLE) return short;
  return `${truncateAtWord(t, MAX_TITLE - suffix.length)}${suffix}`;
}

export const courseSeoTitle = (title: string) => suffixedSeoTitle(title, COURSE_SUFFIX);
export const lessonSeoTitle = (title: string) => suffixedSeoTitle(title, LESSON_SUFFIX);
export const careerSeoTitle = (title: string) => suffixedSeoTitle(title, CAREER_SUFFIX);

/** "{qisqa tavsif} Yo'l: SQL, Excel, Python." (≤155) — qadamlar nomi, sig'guncha */
export function careerSeoDescription(c: { shortDescription: string; steps: { title: string }[] }): string {
  const names = c.steps.map((s) => s.title.trim()).filter(Boolean);
  let path = '';
  for (const n of names) {
    const next = path ? `${path}, ${n}` : n;
    if (next.length > 70) break;
    path = next;
  }
  const suffix = path ? ` Yo'l: ${path}.` : '';
  return truncateAtWord(c.shortDescription, MAX_DESCRIPTION - suffix.length) + suffix;
}

export const COURSE_LEVEL_UZ: Record<ApiCourseLevel, string> = {
  BEGINNER: "Boshlang'ich",
  INTERMEDIATE: "O'rta",
  ADVANCED: 'Yuqori',
};

/** "{qisqa tavsif} N ta video dars, M ta mashq. Daraja: …" (≤155) */
export function courseSeoDescription(c: {
  shortDescription: string;
  lessonCount: number;
  exerciseCount: number;
  level: ApiCourseLevel;
}): string {
  const exercises = c.exerciseCount > 0 ? `, ${c.exerciseCount} ta mashq` : '';
  const suffix = ` ${c.lessonCount} ta video dars${exercises}. Daraja: ${COURSE_LEVEL_UZ[c.level].toLowerCase()}.`;
  return truncateAtWord(c.shortDescription, MAX_DESCRIPTION - suffix.length) + suffix;
}

/** Dars qisqa tavsifi (bo'lmasa sarlavhadan jumla) + " «Kurs» kursi, {modul}." (≤155) */
export function lessonSeoDescription(l: {
  title: string;
  summary?: string | null;
  courseTitle: string;
  moduleTitle: string;
}): string {
  const suffix = ` «${l.courseTitle}» kursi, ${l.moduleTitle}.`;
  const lead = l.summary?.trim() || `«${l.title.trim()}» — video dars va amaliy mashqlar.`;
  const budget = MAX_DESCRIPTION - suffix.length;
  if (budget < 60) return truncateAtWord(lead, MAX_DESCRIPTION);
  return truncateAtWord(lead, budget) + suffix;
}

/* ------------------------------------------------------------- O'rganish (/learn) */

const LEARN_SUFFIX = ' — SQL darslik';

/** "{title} — SQL darslik | SQLMaster.uz" (≤60) */
export const learnSeoTitle = (title: string) => suffixedSeoTitle(title, LEARN_SUFFIX);

/** Post qisqa tavsifi (bo'lmasa matn boshi) — ≤155, so'z chegarasida */
export function learnSeoDescription(p: { title: string; summary?: string | null; contentMd?: string | null }): string {
  const lead =
    p.summary?.trim() || statementLead(p.contentMd) || `«${p.title.trim()}» — SQL darslik: misollar va amaliy masalalar.`;
  return truncateAtWord(lead.replace(/\s+/g, ' '), MAX_DESCRIPTION);
}

export const LEARN_INDEX_SEO = {
  title: "SQL darsliklar — misollar bilan o'rganing | SQLMaster.uz",
  description:
    "SQL bo'yicha qisqa darsliklar: JOIN, GROUP BY, window funksiyalar, CTE va boshqalar — tushuntirish, misol so'rov va natija, oxirida amaliy masalalar.",
};
